/**
 * Who a signed-out seller is and where their item ships from, kept in this
 * browser until the emailed code proves the address is theirs.
 *
 * The listing itself is already kept here -- its fields in localStorage
 * (ListingProvider, `postData`) and its photos in IndexedDB -- so nothing about
 * a guest's listing reaches the server before they sign in. This is the rest:
 * the name, email and address typed into the shipping step, and whether a code
 * is out and the listing should go up the moment they are signed in.
 *
 * `codeSentAt` is what makes that last part safe. A code that was never
 * entered must not post the listing days later when they sign in for some
 * other reason, so the pending publish lapses with the code.
 *
 * Every read and write survives blocked storage: losing the draft costs the
 * seller some typing, breaking the sell page costs the listing.
 */
import { z } from 'zod';
import { LS_Delete, LS_Get, LS_Set } from './local-storage';
import { GuestShippingAddressSchema, type GuestShippingAddress } from '../schemas/components/guest-address-autocomplete';
import type { CreateAddressRequest } from '../schemas/api/addresses/create';

export const GUEST_SELL_DRAFT_KEY = 'guestSellDraft';

/** How long a sent code may still post the listing once it is entered. */
export const GUEST_SELL_PUBLISH_WINDOW_MS = 60 * 60 * 1000;

export const GuestSellDraftSchema = z.object({
  email: z.string().max(254).optional(),
  address: GuestShippingAddressSchema.optional(),
  /** When the sign-in code went out; absent when none is pending. */
  codeSentAt: z.number().optional(),
  /**
   * When a sign-in redeemed that code and the listing started going up. The
   * login step reads the name a moment AFTER the sell page sees the sign-in
   * and clears `codeSentAt` -- next-auth refreshes the session before
   * `signIn()` returns -- so the name is keyed on this as well.
   */
  codeUsedAt: z.number().optional(),
  /**
   * Choose Shipping has been pressed for this listing, so a reload lands back
   * on the shipping step with Finish, not on Choose Shipping again.
   */
  shippingStepOpen: z.boolean().optional(),
  savedAt: z.number(),
});

export type GuestSellDraft = z.infer<typeof GuestSellDraftSchema>;

export const EMPTY_GUEST_ADDRESS: GuestShippingAddress = {
  name: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  postal_code: '',
  country: 'US',
};

/** The saved draft, or null when there is none or it is junk. */
export function readGuestSellDraft(): GuestSellDraft | null {
  try {
    const parsed = GuestSellDraftSchema.safeParse(LS_Get(GUEST_SELL_DRAFT_KEY));
    return parsed.success ? parsed.data : null;
    // eslint-disable-next-line sellwild-code/catch-must-log -- blocked storage is a browser setting, not a fault; logging it would file one error per private-window page view
  } catch {
    return null;
  }
}

/** Merge these fields into the saved draft. `undefined` clears a field. */
export function saveGuestSellDraft(
  patch: Omit<Partial<GuestSellDraft>, 'savedAt'>,
  now: number = Date.now(),
): void {
  try {
    LS_Set(GUEST_SELL_DRAFT_KEY, { ...readGuestSellDraft(), ...patch, savedAt: now });
    // eslint-disable-next-line sellwild-code/catch-must-log -- blocked or full storage, as above: expected, and costs only the convenience
  } catch {
    // The seller keeps typing; they lose the draft on reload.
  }
}

export function clearGuestSellDraft(): void {
  try {
    LS_Delete(GUEST_SELL_DRAFT_KEY);
    // eslint-disable-next-line sellwild-code/catch-must-log -- blocked storage, as above
  } catch {
    // Blocked storage has nothing to clear.
  }
}

/** Is a code out whose entry should still post the listing? */
export function hasPendingGuestPublish(
  draft: GuestSellDraft | null,
  now: number = Date.now(),
): boolean {
  return !!draft?.codeSentAt && now - draft.codeSentAt < GUEST_SELL_PUBLISH_WINDOW_MS;
}

/**
 * The name typed into the shipping step, for an account the code is about to
 * create -- and only when the code went to the same address. The login modal
 * asks a nameless new account for its name; this one already gave it.
 */
export function pendingGuestSellerName(
  email: string | null | undefined,
  now: number = Date.now(),
): string | null {
  const draft = readGuestSellDraft();
  const codeAt = draft?.codeSentAt ?? draft?.codeUsedAt;
  if (!email || !codeAt || now - codeAt >= GUEST_SELL_PUBLISH_WINDOW_MS) return null;
  if (draft?.email?.trim().toLowerCase() !== email.trim().toLowerCase()) return null;
  const name = draft.address?.name?.trim();
  return name || null;
}

/** COUNTRY.id of the United States, the same on staging and prod. */
export const US_COUNTRY_ID = 237;

/**
 * The typed address as the address book stores it. The name goes on as
 * `fullName` too: the addresses route gives a nameless account the name on
 * its first address, which is a second chance if the sign-in step's own save
 * of it failed.
 */
export function guestAddressToCreateRequest(address: GuestShippingAddress): CreateAddressRequest {
  return {
    fullName: address.name.trim(),
    shippingName: address.name.trim(),
    line1: address.line1.trim(),
    line2: address.line2?.trim() || null,
    locality: address.city.trim(),
    region: address.state.trim(),
    zipCode: address.postal_code.trim(),
    // Sellwild ships within the US only. The US is COUNTRY 237; 1 is
    // Afghanistan on prod, which is where this used to file every guest seller.
    countryId: US_COUNTRY_ID,
    isDefault: 1,
  };
}
